import React from "react";
import { GridColDef } from "@mui/x-data-grid";
import { Chip, IconButton, Tooltip } from "@mui/material";
import { ReceiptLongOutlined } from "@mui/icons-material";
import { DeleteButton } from "@refinedev/mui";

import { TableCell } from "@shared/components";
import { Notification } from "@modules/notifications/types";

interface NotificationColumnsProps {
  onRefetch: () => void;
  onOpenLogs: (id: number) => void;
  translate: (key: string, options?: any) => string;
}

export const useNotificationsListColumns = ({
  onRefetch,
  onOpenLogs,
  translate,
}: NotificationColumnsProps): GridColDef<Notification>[] => {
  return React.useMemo<GridColDef<Notification>[]>(() => {
    const t = (key: string) => translate(`notifications_emails_admin.${key}`);

    const scheduleLabel = (row: Notification) =>
      row.schedule_type === "recurring"
        ? `${(row.days_of_week ?? []).map((day) => t(`days.${day}`).slice(0, 3)).join(", ")} ${row.time?.slice(0, 5) ?? ""}`
        : row.date
          ? new Date(row.date).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
          : "-";

    const status = (row: Notification) => {
      if (row.has_error) return { label: t("status.error"), color: "error" as const };
      if (!row.active) return { label: t("status.inactive"), color: "default" as const };
      if (row.schedule_type === "one_time" && row.last_time_sent)
        return { label: t("status.sent"), color: "success" as const };
      return { label: t("status.scheduled"), color: "info" as const };
    };

    return [
      {
        field: "notification_type",
        headerName: t("fields.notification_type"),
        width: 100,
        renderCell: ({ value }) => (
          <Chip size="small" variant="outlined" label={t(`types.${value}`)} />
        ),
      },
      {
        field: "subject",
        headerName: t("fields.subject"),
        flex: 1,
        minWidth: 220,
        sortable: false,
        valueGetter: (_, row) => row.email_subject || row.push_subject,
      },
      {
        field: "agreement",
        headerName: t("fields.agreement"),
        minWidth: 180,
        valueGetter: (_, row) => row.agreement_name ?? (row.agreement ? `#${row.agreement}` : "-"),
      },
      {
        field: "schedule_type",
        headerName: t("fields.schedule_type"),
        minWidth: 170,
        renderCell: ({ row }) => (
          <Tooltip title={t(`schedule.${row.schedule_type}`)}>
            <span>{scheduleLabel(row)}</span>
          </Tooltip>
        ),
      },
      {
        field: "status",
        headerName: t("fields.status"),
        minWidth: 120,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: ({ row }) => {
          const { label, color } = status(row);
          return (
            <Tooltip title={row.error_log ?? ""}>
              <Chip size="small" color={color} label={label} />
            </Tooltip>
          );
        },
      },
      {
        field: "last_send_result",
        headerName: t("fields.last_send_result"),
        flex: 1,
        minWidth: 240,
        sortable: false,
        renderCell: ({ value }) => (
          <Tooltip title={value ?? ""}>
            <span>{value ?? "-"}</span>
          </Tooltip>
        ),
      },
      {
        field: "last_time_sent",
        headerName: t("fields.last_time_sent"),
        minWidth: 160,
        headerAlign: "center",
        renderCell: ({ value }) => (value ? <TableCell.Date value={value} align="center" /> : "-"),
      },
      {
        field: "created_at",
        headerName: t("fields.created_at"),
        minWidth: 160,
        headerAlign: "center",
        renderCell: ({ value }) => <TableCell.Date value={value} align="center" />,
      },
      {
        field: "actions",
        headerName: translate("table.actions"),
        headerAlign: "center",
        width: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <TableCell.Container align="center">
            <Tooltip title={t("send_logs.open")}>
              <IconButton size="small" onClick={() => onOpenLogs(row.id)}>
                <ReceiptLongOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <DeleteButton
              hideText
              resource="v1/notifications_emails_admin"
              recordItemId={row.id}
              onSuccess={onRefetch}
            />
          </TableCell.Container>
        ),
      },
    ];
  }, [translate, onRefetch, onOpenLogs]);
};
